import React from 'react';

import { IconAutomation } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Divider,
  LoadingIndicator,
  Modal,
  Text,
} from '@cfa/react-core';

import usePlanOwner from '@/hooks/usePlanOwner';

import { cn } from '@/utils/tailwind';

import { Plan } from '@/_generated/api-clients/xp';

const MODAL_OVERLAY_CLASS = 'z-[1251]';
const DAYS_PER_YEAR = 365;

interface EnableCompliancePlanModalProps {
  plan: Plan;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Enable Compliance Plan modal.
 * Self-contained modal using render-at-use pattern.
 */
export const EnableCompliancePlanModal: React.FC<
  EnableCompliancePlanModalProps
> = ({ plan, isPending = false, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const { shouldSelectOwner } = usePlanOwner();

  const getRecertificationPeriod = (): string => {
    const days = plan.expireAfterCompletionOffsetInDays;
    if (!days) {
      return t(
        'Settings.enableCompliancePlanModal.recertificationPeriod.oneYear',
      );
    }
    const years = Math.round(days / DAYS_PER_YEAR);
    return years >= 3
      ? t('Settings.enableCompliancePlanModal.recertificationPeriod.threeYears')
      : t('Settings.enableCompliancePlanModal.recertificationPeriod.oneYear');
  };

  return (
    <Modal.Overlay
      isOpen
      isDismissable={false}
      onOpenChange={isOpen => !isOpen && onClose()}
      className={cn(MODAL_OVERLAY_CLASS)}
    >
      <Modal.Dialog
        size="lg"
        className="w-[600px]! [--dialog-content-gap:32px]! [--dialog-padding-sides:32px]!"
      >
        <Modal.Title className="text-center">
          <Text className="text-grayscale-gray6! text-2xl! font-bold!">
            {t('Settings.enableCompliancePlanModal.title')}
          </Text>
        </Modal.Title>
        <Modal.Content className="max-w-none! items-stretch! px-8! pt-3! text-left!">
          <Divider />
          <div className="text-grayscale-gray7 flex flex-col px-3 py-2 text-left text-base/6">
            <p>{t('Settings.enableCompliancePlanModal.intro')}</p>
            <ul className="mt-2 list-none space-y-2 pl-4">
              <li className="flex items-start gap-2.5">
                <IconAutomation
                  className="text-grayscale-gray7 mt-1 shrink-0"
                  size={16}
                  stroke={1.5}
                />
                <span className="flex-1">
                  {t('Settings.enableCompliancePlanModal.bullet1')}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconAutomation
                  className="text-grayscale-gray7 mt-1 shrink-0"
                  size={16}
                  stroke={1.5}
                />
                <span className="flex-1">
                  {t('Settings.enableCompliancePlanModal.bullet2')}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <IconAutomation
                  className="text-grayscale-gray7 mt-1 shrink-0"
                  size={16}
                  stroke={1.5}
                />
                <span className="flex-1">
                  {t('Settings.enableCompliancePlanModal.bullet3', {
                    period: getRecertificationPeriod(),
                  })}
                </span>
              </li>
            </ul>
            <p className="mt-4">
              {shouldSelectOwner
                ? t(
                    'Settings.enableCompliancePlanModal.footerMultipleOperators',
                  )
                : t('Settings.enableCompliancePlanModal.footer')}
            </p>
          </div>
          <Divider />
        </Modal.Content>
        <Modal.Footer className="px-9! pt-4!">
          <div className="flex w-full flex-wrap gap-2">
            <Button
              color="secondary"
              variant="outlined"
              className="min-w-[200px]! flex-1! sm:w-[260px]!"
              onPress={onClose}
            >
              {t('Button.cancel')}
            </Button>
            <Button
              color="secondary"
              variant="filled"
              className="min-w-[200px]! flex-1! sm:w-[260px]!"
              isPending={isPending}
              isDisabled={isPending}
              onPress={onConfirm}
            >
              {isPending && <LoadingIndicator size="sm" />}
              {t('Button.enable')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Dialog>
    </Modal.Overlay>
  );
};
